import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useDebounce } from '@/hooks/use-debounce';
import { formatCurrency } from '@/lib/utils';
import { Store } from '@/types/store';
import { ColumnFilter } from '@tanstack/react-table';

export interface FilterSidebarRangeFilterProps {
  filter: ColumnFilter;
  setFilter: (values: number[]) => void;
  onRemoveFilter?: () => void;
  min: number;
  max: number;
  labelMinValue: string;
  labelMaxValue: string;
  placeholderMinValue: string;
  placeholderMaxValue: string;
  store: Store;
  displayUnit?: string;
}

const FilterSidebarRangeFilter: React.FC<FilterSidebarRangeFilterProps> = ({
  filter,
  setFilter,
  onRemoveFilter,
  min,
  max,
  labelMinValue,
  labelMaxValue,
  placeholderMinValue,
  placeholderMaxValue,
  store,
  displayUnit,
}) => {
  const { t } = useTranslation();

  // Shared helper function to validate and clamp filter values
  const validateAndClampFilterValues = useCallback(
    (filterValue: unknown): [number, number] => {
      // Ensure min <= max (edge case protection)
      if (min > max) {
        return [max, max] as [number, number];
      }

      // Check if filter value is valid array
      if (!Array.isArray(filterValue) || filterValue.length < 2) {
        return [min, max] as [number, number];
      }

      const currentMin = Number(filterValue[0]);
      const currentMax = Number(filterValue[1]);

      // Validate and clamp values
      const validMin = isNaN(currentMin)
        ? min
        : Math.max(min, Math.min(currentMin, max));
      const validMax = isNaN(currentMax)
        ? max
        : Math.min(max, Math.max(currentMax, min));

      // Ensure validMin <= validMax
      return [Math.min(validMin, validMax), Math.max(validMin, validMax)] as [
        number,
        number,
      ];
    },
    [min, max]
  );

  // Validate and get initial filter values safely
  const getInitialValues = useCallback(() => {
    return validateAndClampFilterValues(filter.value);
  }, [filter.value, validateAndClampFilterValues]);

  const [values, setValues] = useState<[number, number]>(() =>
    getInitialValues()
  );
  // Local state for inputs to allow temporary empty state while typing
  const [minInput, setMinInput] = useState<string>(() =>
    String(values[0] ?? '')
  );
  const [maxInput, setMaxInput] = useState<string>(() =>
    String(values[1] ?? '')
  );
  // Track if user is currently editing to prevent sync overwriting their input
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);

  // Sync input strings when values change externally (e.g., from slider or prop changes)
  // Only sync if user is not currently editing that input
  useEffect(() => {
    if (!isEditingMin) {
      setMinInput(String(values[0] ?? ''));
    }
    if (!isEditingMax) {
      setMaxInput(String(values[1] ?? ''));
    }
  }, [values, isEditingMin, isEditingMax]);

  // Update values when min/max change (e.g., when switching views)
  // Clamp the current filter values to the new min/max range
  useEffect(() => {
    const validatedValues = validateAndClampFilterValues(filter.value);
    setValues(validatedValues);
  }, [min, max, filter.value, validateAndClampFilterValues]);

  const setFilterCallback = useCallback(
    (vals: number[]) => setFilter(vals),
    [setFilter]
  );

  const debouncedSetFilter = useDebounce(setFilterCallback, 100);

  const onChange = useCallback(
    (newValues: number[]) => {
      setValues(newValues as [number, number]);
      debouncedSetFilter(newValues);
    },
    [debouncedSetFilter]
  );

  const onReset = () => {
    // If onRemoveFilter is provided, completely remove the filter
    // Otherwise, reset to full range (backward compatibility)
    if (onRemoveFilter) {
      onRemoveFilter();
    } else {
      setValues([min, max]);
      setFilter([min, max]);
    }
  };

  return (
    <div className="space-y-4 px-2 pt-2">
      {/* Slider */}
      <div className="space-y-2">
        <Slider
          value={values}
          onValueChange={onChange}
          min={min}
          max={max}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-gray-500">
          <span>
            {displayUnit === '%'
              ? `${min.toFixed(2)}%`
              : formatCurrency(min, store)}
          </span>
          <span>
            {displayUnit === '%'
              ? `${max.toFixed(2)}%`
              : formatCurrency(max, store)}
          </span>
        </div>
      </div>

      {/* Inputs min et max */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-700">
            {labelMinValue}
          </Label>
          <Input
            value={minInput}
            onFocus={() => setIsEditingMin(true)}
            onChange={(e) => {
              const inputValue = e.target.value;
              setMinInput(inputValue); // Update local state immediately

              // Prevent negative numbers for prices/stock
              if (inputValue.startsWith('-')) {
                setMinInput(inputValue.replace('-', ''));
                return;
              }

              if (inputValue === '') {
                // Allow empty temporarily - don't update values yet
                return;
              }

              const numValue = parseFloat(inputValue);
              if (!isNaN(numValue)) {
                const clampedValue = Math.max(min, Math.min(numValue, max));
                onChange([clampedValue, (values as number[])[1]]);
              }
            }}
            onBlur={(e) => {
              setIsEditingMin(false);
              const inputValue = e.target.value;

              if (inputValue === '') {
                // If empty, set to min
                onChange([min, (values as number[])[1]]);
                return;
              }

              const numValue = parseFloat(inputValue);
              if (isNaN(numValue)) {
                // If invalid, set to min
                onChange([min, (values as number[])[1]]);
                return;
              }

              // Clamp to valid range if out of bounds
              const clampedValue = Math.max(min, Math.min(numValue, max));
              if (clampedValue !== numValue) {
                onChange([clampedValue, (values as number[])[1]]);
              }
            }}
            type="number"
            inputMode="numeric"
            placeholder={placeholderMinValue}
            className="h-9"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-gray-700">
            {labelMaxValue}
          </Label>
          <Input
            value={maxInput}
            onFocus={() => setIsEditingMax(true)}
            onChange={(e) => {
              const inputValue = e.target.value;
              setMaxInput(inputValue); // Update local state immediately

              // Prevent negative numbers for prices/stock
              if (inputValue.startsWith('-')) {
                setMaxInput(inputValue.replace('-', ''));
                return;
              }

              if (inputValue === '') {
                // Allow empty temporarily - don't update values yet
                return;
              }

              const numValue = parseFloat(inputValue);
              if (!isNaN(numValue)) {
                const clampedValue = Math.max(min, Math.min(numValue, max));
                onChange([(values as number[])[0], clampedValue]);
              }
            }}
            onBlur={(e) => {
              setIsEditingMax(false);
              const inputValue = e.target.value;

              if (inputValue === '') {
                // If empty, set to max
                onChange([(values as number[])[0], max]);
                return;
              }

              const numValue = parseFloat(inputValue);
              if (isNaN(numValue)) {
                // If invalid, set to max
                onChange([(values as number[])[0], max]);
                return;
              }

              // Clamp to valid range if out of bounds
              const clampedValue = Math.max(min, Math.min(numValue, max));
              if (clampedValue !== numValue) {
                onChange([(values as number[])[0], clampedValue]);
              }
            }}
            type="number"
            inputMode="numeric"
            placeholder={placeholderMaxValue}
            className="h-9"
          />
        </div>
      </div>

      <Button
        variant="outline"
        onClick={onReset}
        size="sm"
        className="h-9 w-full text-xs"
      >
        {t('components.filter_sidebar.reset')}
      </Button>
    </div>
  );
};

export default FilterSidebarRangeFilter;
